import React, { useState } from 'react';
import {
  Box,
  Flex,
  VStack,
  IconButton,
  useColorModeValue,
  Text,
  Button,
  Avatar,
  Divider,
  HStack,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useToast,
} from '@chakra-ui/react';
import { HamburgerIcon, ViewIcon, TimeIcon, ExternalLinkIcon, ChevronLeftIcon, ChevronRightIcon, CalendarIcon, SearchIcon, WarningIcon } from '@chakra-ui/icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { ColorModeToggle } from './ColorModeToggle';
import { BiBrain } from 'react-icons/bi'; // or any other icon you prefer

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItemProps {
  path: string;
  icon: JSX.Element;
  label: string;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { user, setUpstoxToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      // Clear Upstox token
      setUpstoxToken('');
      // Redirect to login
      navigate('/');
      toast({
        title: 'Logged out successfully',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: 'Error logging out',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const NavItem: React.FC<NavItemProps> = ({ path, icon, label }) => (
    <Button
      leftIcon={icon}
      variant={location.pathname === path ? 'solid' : 'ghost'}
      justifyContent="start"
      width="full"
      onClick={() => navigate(path)}
    >
      {label}
    </Button>
  );

  const SidebarContent = () => (
    <VStack spacing={4} align="stretch" w="full" position="relative">
      <HStack p={4} spacing={4} justify="flex-start">
        <Avatar size="sm" name={user?.email || undefined} />
        {!isSidebarCollapsed && (
          <Text fontWeight="bold" isTruncated>
            {user?.email}
          </Text>
        )}
      </HStack>
      <Divider />
      <VStack spacing={2} align="stretch" px={4}>
        <NavItem 
          path="/dashboard" 
          icon={<ViewIcon />} 
          label={isSidebarCollapsed ? "" : "Home"} 
        />
        <NavItem 
          path="/market-search" 
          icon={<SearchIcon />} 
          label={isSidebarCollapsed ? "" : "Market Search"} 
        />
        <NavItem 
          path="/holdings" 
          icon={<TimeIcon />} 
          label={isSidebarCollapsed ? "" : "Portfolio"} 
        />
        <NavItem 
          path="/earnings-calendar" 
          icon={<CalendarIcon />} 
          label={isSidebarCollapsed ? "" : "Earnings Calendar"} 
        />
        <NavItem 
          path="/advisor" 
          icon={<BiBrain />} 
          label={isSidebarCollapsed ? "" : "AI Advisor"} 
        />
        <NavItem 
          path="/scam-aware" 
          icon={<WarningIcon color="red.500" />} 
          label={isSidebarCollapsed ? "" : "Scam Aware"} 
        />
      </VStack>
      <Divider />
      <Box px={4}>
        <Button
          leftIcon={<ExternalLinkIcon />}
          colorScheme="red"
          variant="ghost"
          width="full"
          onClick={handleLogout}
        >
          {!isSidebarCollapsed && "Logout"}
        </Button>
      </Box>
      <IconButton
        aria-label="Toggle sidebar"
        icon={isSidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        position="absolute"
        right="-3"
        top="50%"
        transform="translateY(-50%)"
        borderRadius="full"
        size="sm"
        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        zIndex={2}
        boxShadow="md"
      />
    </VStack>
  );

  return (
    <Flex 
      h="100vh" 
      bg={useColorModeValue('gray.50', 'gray.900')}
      flexDir={{ base: 'column', lg: 'row' }}
    >
      <Box
        display={{ base: 'none', lg: 'block' }}
        w={isSidebarCollapsed ? "80px" : "240px"}
        minW={isSidebarCollapsed ? "80px" : "240px"}
        bg={useColorModeValue('white', 'gray.800')}
        borderRight="1px"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
        transition="width 0.2s"
        position="relative"
      >
        <SidebarContent />
      </Box>

      <Box flex={1} overflow="auto" w={{ base: '100%', lg: 'auto' }}>
        <Flex
          pos="sticky"
          top={0}
          zIndex={10}
          bg={useColorModeValue('white', 'gray.800')}
          borderBottom="1px"
          borderColor={useColorModeValue('gray.200', 'gray.700')}
          p={{ base: 2, md: 4 }}
          justify="space-between"
          align="center"
        >
          <IconButton
            display={{ base: 'block', lg: 'none' }}
            aria-label="Menu"
            icon={<HamburgerIcon />}
            onClick={onOpen}
            variant="ghost"
          />
          
          <Box ml="auto">
            <ColorModeToggle />
          </Box>
        </Flex>

        <Box p={{ base: 2, md: 4 }}>
          {children}
        </Box>
      </Box>

      <Drawer 
        isOpen={isOpen} 
        placement="left" 
        onClose={onClose}
        size={{ base: "full", sm: "xs" }}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Menu</DrawerHeader>
          <DrawerBody p={0}>
            <SidebarContent />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
};
